// مشترک صفحات CLN (AP-CLN-01): شناسه صفحات، مسیرها (IA §2.3: /desk، /desk/encounters/:id)، کلیدهای Query، نوار هشدار بالینی
// (COMP-CLINICAL-ALERTS)، دیالوگ PIN امضا و دیالوگ Override منع مصرف (COMP-OVERRIDE؛ فقط cln.alert.override — BR-CLN-062).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type ClnClinicalAlertView,
  type ClnClinicalAlertsResponse,
  type ClnFindingView,
  type ClnNoteStatus,
  type ClnOverrideReason,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { FormDialog, SampleBadge, StatusChip, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

/** میز کار ارائه‌دهنده (US-CLN-080، 082). */
export const SCR_STAFF_500 = 'SCR-STAFF-500';
/** پرونده بالینی (US-CLN-001 تا 006، 031، 090). */
export const SCR_STAFF_501 = 'SCR-STAFF-501';
/** مواجهه (US-CLN-010 تا 012، 020 تا 025، 030، 040). */
export const SCR_STAFF_502 = 'SCR-STAFF-502';
/** کارهای معوق (US-CLN-081). */
export const SCR_STAFF_506 = 'SCR-STAFF-506';
/** قالب‌ها، عبارات سریع، فهرست تشخیص و قواعد منع مصرف (US-CLN-020، 021، 030، 061). */
export const SCR_STAFF_508 = 'SCR-STAFF-508';
/** یادآوری بالینی (US-CLN-154). */
export const SCR_STAFF_517 = 'SCR-STAFF-517';

export const DESK_PATH = '/desk';
export const PENDING_PATH = '/desk/pending';
export const REMINDERS_PATH = '/desk/reminders';
export const CLINICAL_SETTINGS_PATH = '/settings/clinical';
export const encounterPath = (id: string) => `${DESK_PATH}/encounters/${id}`;
export const remindersPath = (patientId?: string) => (patientId ? `${REMINDERS_PATH}?patientId=${patientId}` : REMINDERS_PATH);
export const API = '/api/v1/clinical';

export const clnKeys = {
  all: (tenantId?: string) => ['clinical', tenantId] as const,
  day: (tenantId: string | undefined, date: string) => ['clinical', tenantId, 'day', date] as const,
  pending: (tenantId?: string) => ['clinical', tenantId, 'pending'] as const,
  overdue: (tenantId?: string) => ['clinical', tenantId, 'overdue'] as const,
  patient: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId] as const,
  alerts: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'alerts'] as const,
  summary: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'summary'] as const,
  section: (tenantId: string | undefined, patientId: string, section: string) => ['clinical', tenantId, 'patient', patientId, section] as const,
  reminders: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'reminders'] as const,
  milestones: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'milestones'] as const,
  historyStatuses: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'history-statuses'] as const,
  encounter: (tenantId: string | undefined, id: string) => ['clinical', tenantId, 'encounter', id] as const,
  note: (tenantId: string | undefined, encounterId: string) => ['clinical', tenantId, 'encounter', encounterId, 'note'] as const,
  templates: (tenantId?: string) => ['clinical', tenantId, 'templates'] as const,
  phrases: (tenantId?: string) => ['clinical', tenantId, 'phrases'] as const,
  rules: (tenantId?: string) => ['clinical', tenantId, 'rules'] as const,
  catalog: (tenantId: string | undefined, query: string) => ['clinical', tenantId, 'catalog', query] as const,
  favorites: (tenantId?: string) => ['clinical', tenantId, 'favorites'] as const,
};

/** عدد از قرارداد (int ممکن است رشته باشد). */
export function num(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return typeof value === 'number' ? value : Number(value);
}

/** کد خطای problem+json. */
export function errorCode(error: unknown): string | undefined {
  return isApiError(error) ? error.code : undefined;
}

const NOTE_TONES: Record<NonNullable<ClnNoteStatus>, StatusTone> = {
  Draft: 'pending',
  Signed: 'done',
  Amended: 'confirmed',
  Voided: 'error',
  Discarded: 'cancelled',
  PendingCosign: 'pending',
};

export function NoteStatusChip({ status }: { status: ClnNoteStatus | null | undefined }) {
  if (!status) {
    return null;
  }
  return <StatusChip tone={NOTE_TONES[status]} labelKey={`cln:note_status.${status}`} />;
}

/** هشدارهای بالینی مراجعه‌کننده (مشاهده در سرور Audit می‌شود). */
export function useClinicalAlerts(patientId: string | null | undefined) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: clnKeys.alerts(session?.tenantId, patientId ?? ''),
    enabled: Boolean(patientId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/alerts', { params: { path: { patientId: patientId ?? '' } } })),
    retry: false,
  });
}

function alertLabel(t: (key: string, values?: Record<string, unknown>) => string, alert: ClnClinicalAlertView): string {
  switch (alert.kind) {
    case 'Allergy':
      return t('cln:alerts.allergy', { agent: alert.text ?? '' });
    case 'AllergyUnreviewed':
      return t('cln:alerts.allergy_unreviewed');
    case 'SpecialCondition':
      return t(`cln:status_kind.${alert.code}`) + (alert.text ? ` — ${alert.text}` : '');
    case 'ConditionNeedsReview':
      return t('cln:alerts.needs_review', { status: t(`cln:status_kind.${alert.code}`) });
    case 'SensitiveMedication':
      return t('cln:alerts.medication', { name: alert.text ?? '', drugClass: t(`cln:drug_class.${alert.code}`) });
    case 'HighRiskProblem':
      return t('cln:alerts.problem', { title: alert.text ?? '' });
    default:
      return alert.text ?? '';
  }
}

/** COMP-CLINICAL-ALERTS: نوار هشدار بالینی (بحرانی قرمز، هشدار زرد؛ «آلرژی بررسی نشده» نشان زرد — AC-CLN-060.1). */
export function ClinicalAlertsBar({ data, compact = false }: { data: ClnClinicalAlertsResponse | undefined; compact?: boolean }) {
  const { t } = useTranslation('cln');
  if (!data) {
    return null;
  }
  // AC-CLN-114.2 (C-AGENT-906): نشان خطر High/Imminent فقط وقتی سرور برای نقش بالینی بفرستد؛ فقط سطح، بدون عوامل یا قضاوت.
  const risk = data.risk ? (
    <Chip
      size="small"
      color="error"
      variant={data.risk.level === 'Imminent' ? 'filled' : 'outlined'}
      label={t(data.risk.hasActiveSafetyPlan ? 'cln:alerts.risk' : 'cln:alerts.risk_no_plan', { level: t(`cln:psych.level.${data.risk.level}`) })}
      data-testid="cln-risk-badge"
      data-level={data.risk.level}
    />
  ) : null;
  if (data.items.length === 0 && !risk) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="clinical-alerts-none">
        {t('cln:alerts.none')}
      </Typography>
    );
  }
  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      role="region"
      aria-label={t('cln:alerts.title')}
      data-testid="clinical-alerts"
      sx={{ alignItems: 'center' }}
    >
      {!compact && (
        <Typography variant="subtitle2" component="span">
          {t('cln:alerts.title')}
        </Typography>
      )}
      {risk}
      {data.items.map((alert, index) => (
        <Chip
          key={`${alert.kind}-${alert.sourceId ?? index}`}
          size="small"
          color={alert.severity === 'Critical' ? 'error' : 'warning'}
          variant={alert.kind === 'AllergyUnreviewed' ? 'outlined' : 'filled'}
          label={alertLabel(t, alert)}
          data-testid="clinical-alert"
          data-kind={alert.kind}
          data-severity={alert.severity}
        />
      ))}
    </Stack>
  );
}

/** دیالوگ PIN امضا (BR-CLN-021؛ همان PIN تأیید شخصی — C-AGENT-637). PIN هرگز ذخیره یا لاگ نمی‌شود. */
export function PinDialog({
  open,
  titleKey,
  onClose,
  onConfirm,
  isSubmitting,
  error,
  children,
}: {
  open: boolean;
  titleKey: string;
  onClose: () => void;
  onConfirm: (pin: string) => void;
  isSubmitting: boolean;
  error: unknown;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation('cln');
  const [pin, setPin] = useState('');
  const code = errorCode(error);
  return (
    <FormDialog
      open={open}
      titleKey={titleKey}
      submitKey="cln:sign.confirm"
      onSubmit={() => onConfirm(pin)}
      onClose={() => {
        setPin('');
        onClose();
      }}
      isDirty={false}
      isSubmitting={isSubmitting}
      submitError={code === 'PHX-CLN-0207' || code === 'PHX-CLN-0208' || code === 'PHX-CLN-0209' ? undefined : error}
    >
      {children}
      <TextField
        label={t('cln:sign.pin')}
        type="password"
        value={pin}
        autoFocus
        autoComplete="off"
        onChange={(event) => setPin(event.target.value)}
        slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', maxLength: 12, 'data-testid': 'cln-pin' } }}
        error={code === 'PHX-CLN-0207' || code === 'PHX-CLN-0208' || code === 'PHX-CLN-0209'}
        helperText={
          code === 'PHX-CLN-0207'
            ? t('cln:sign.pin_invalid', { remaining: isApiError(error) ? Number(error.extensions.remaining ?? 0) : 0 })
            : code === 'PHX-CLN-0208'
              ? t('cln:sign.pin_locked')
              : code === 'PHX-CLN-0209'
                ? t('cln:sign.pin_not_set')
                : t('cln:sign.pin_hint')
        }
      />
    </FormDialog>
  );
}

const OVERRIDE_REASONS: readonly ClnOverrideReason[] = ['ClinicalJudgment', 'BenefitOutweighsRisk', 'ConditionResolved', 'InformedConsent'];

/**
 * COMP-OVERRIDE: یافته‌های منع مصرف و Override با دلیل فهرست + متن (AC-CLN-062.1). بدون cln.alert.override فقط پیام «نیازمند پزشک» نمایش داده
 * می‌شود (Therapist و Nurse منع مطلق را Override نمی‌کنند).
 */
export function OverrideDialog({
  open,
  patientId,
  findings,
  canOverride,
  appointmentId,
  onClose,
  onOverridden,
}: {
  open: boolean;
  patientId: string;
  findings: readonly ClnFindingView[];
  canOverride: boolean;
  appointmentId?: string | null;
  onClose: () => void;
  onOverridden: () => void;
}) {
  const { t } = useTranslation('cln');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const [reason, setReason] = useState<ClnOverrideReason>('ClinicalJudgment');
  const [text, setText] = useState('');
  const pending = findings.filter((finding) => finding.level === 'Absolute' && !finding.overrideId);
  const save = useMutation({
    mutationFn: async () => {
      for (const finding of pending) {
        await unwrap(
          tenantApi.POST('/api/v1/clinical/patients/{patientId}/alert-overrides', {
            params: { path: { patientId } },
            body: { ruleId: finding.ruleId, serviceId: finding.serviceId, appointmentId: appointmentId ?? null, reason, reasonText: text.trim() },
            headers: { 'Idempotency-Key': key.current() },
          }),
        );
      }
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setText('');
      onOverridden();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="cln:override.title"
      submitKey={canOverride ? 'cln:override.submit' : 'cln:override.close'}
      onSubmit={() => (canOverride && text.trim() ? save.mutate() : canOverride ? undefined : onClose())}
      onClose={onClose}
      isDirty={text.trim() !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
      screenId="COMP-OVERRIDE"
    >
      <Alert severity="error" data-testid="cln-override-blocked">
        {t('cln:override.blocked')}
      </Alert>
      <Box component="ul" sx={{ m: 0, ps: 3 }} data-testid="cln-override-findings">
        {findings.map((finding) => (
          <li key={`${finding.ruleId}-${finding.serviceId}`}>
            <Typography variant="body2" component="span">
              <strong>{t(`cln:level.${finding.level}`)}</strong> · {finding.message}
            </Typography>{' '}
            {finding.isSample ? <SampleBadge /> : null}
          </li>
        ))}
      </Box>
      {canOverride ? (
        <>
          <TextField
            select
            label={t('cln:override.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value as ClnOverrideReason)}
            slotProps={{ htmlInput: { 'data-testid': 'cln-override-reason' } }}
          >
            {OVERRIDE_REASONS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`cln:override_reason.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('cln:override.text')}
            value={text}
            required
            multiline
            minRows={2}
            onChange={(event) => setText(event.target.value)}
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'cln-override-text' } }}
          />
        </>
      ) : (
        <Alert severity="info" data-testid="cln-override-physician-only">
          {t('cln:override.physician_only')}
        </Alert>
      )}
    </FormDialog>
  );
}

/** نشان «نمونه» برای محتوای IsSample (CLAUDE.md §10). */
export function MaybeSample({ isSample }: { isSample: boolean | undefined }) {
  return isSample ? <SampleBadge /> : null;
}
